import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, Grid, IconButton, Tooltip } from '@mui/material';
import {
  CellClassParams,
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGridPage } from '../../components/data-grid';
import { FilterTextField } from '../../components/filters';
import FilterSelectField from '../../components/filters/filter-select-field.tsx';
import { useInteractiveGrid } from '../../hooks';
import { useAppSelector } from '../../store/hooks';
import { Manufacturer, ManufacturerFilters } from '../../types';
import { formatPhoneNumber, getStatusLabel } from '../../utils';
import { selectAccessManufacturers, selectManageManufacturers } from '../user';
import { useGetManufacturersQuery, usePrefetch } from './manufacturers-api-slice';

const EditManufacturerCell = (params: ICellRendererParams) => {
  const navigate = useNavigate();
  const canManage = useAppSelector(selectManageManufacturers);

  return (
    <Tooltip title={canManage ? 'Edit Manufacturer' : 'View Manufacturer'}>
      <IconButton
        size="small"
        onClick={() => navigate(`/manufacturer-profile/${params.data.id}`)}
        aria-label={canManage ? 'edit manufacturer' : 'view manufacturer'}
      >
        {canManage ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

const ManufacturerManagementPage = () => {
  const hasAccess = useAppSelector(selectAccessManufacturers);
  const canManageManufacturers = useAppSelector(selectManageManufacturers);
  const prefetchManufacturers = usePrefetch('getManufacturers');
  const navigate = useNavigate();

  const {
    gridContainerRef,
    pagination,
    filters,
    filtersExpanded,
    setFiltersExpanded,
    response: manufacturersResponse,
    error,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    onSortChanged,
    handleClearFilters,
    handleNextPageHover,
  } = useInteractiveGrid<ManufacturerFilters, Manufacturer>({
    hasAccess,
    prefetchFunction: prefetchManufacturers,
    getDataQuery: useGetManufacturersQuery,
    initialFilters: {
      status: 'true',
    },
  });

  // Column definitions for AG Grid
  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: 'licenseeName',
        headerName: 'Manufacturer Name',
        sortable: true,
        flex: 1.5,
      },
      {
        field: 'code',
        headerName: 'Code',
        sortable: true,
        width: 80,
      },
      {
        field: 'city',
        headerName: 'City',
        sortable: true,
        flex: 1,
      },
      {
        field: 'state',
        headerName: 'State',
        sortable: true,
        width: 100,
      },
      {
        field: 'phone',
        headerName: 'Phone',
        sortable: true,
        width: 150,
        valueFormatter: (params: ValueFormatterParams) => formatPhoneNumber(params.data?.phone),
      },
      {
        field: 'email',
        headerName: 'Email',
        sortable: true,
        flex: 1.5,
      },
      {
        field: 'subscription',
        headerName: 'Subscription',
        sortable: true,
        width: 85,
        valueFormatter: (params: ValueFormatterParams) => getStatusLabel(params.data?.status),
        cellStyle: (params: CellClassParams<Manufacturer, string>) => {
          return params.data?.status ? { color: 'green' } : { color: 'red' };
        },
      },
      {
        headerName: 'Actions',
        width: 80,
        sortable: false,
        cellRenderer: EditManufacturerCell,
      },
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      { value: 'true', label: getStatusLabel(true) },
      { value: 'false', label: getStatusLabel(false) },
    ],
    []
  );

  if (!hasAccess) {
    return null;
  }

  const renderFilterControls = () => {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6} md={2}>
          <FilterTextField
            label="Manufacturer Name"
            value={filters.licenseeName ?? ''}
            onChange={value => handleFilterChange('licenseeName', value)}
            hasValue={!!filters.licenseeName}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="Code"
            value={filters.code ?? ''}
            onChange={value => handleFilterChange('code', value)}
            hasValue={!!filters.code}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="City"
            value={filters.city ?? ''}
            onChange={value => handleFilterChange('city', value)}
            hasValue={!!filters.city}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="State"
            value={filters.state ?? ''}
            onChange={value => handleFilterChange('state', value)}
            hasValue={!!filters.state}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="Phone"
            value={filters.phone ?? ''}
            onChange={value => handleFilterChange('phone', value)}
            hasValue={!!filters.phone}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FilterTextField
            label="Email"
            value={filters.email ?? ''}
            onChange={value => handleFilterChange('email', value)}
            hasValue={!!filters.email}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterSelectField
            label="Status"
            value={filters.status ?? ''}
            onChange={value => handleFilterChange('status', value === '' ? undefined : value)}
            hasValue={filters.status !== undefined && filters.status !== ''}
            options={statusOptions}
            allOptionLabel="All Status"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            fullWidth
            size="small"
            sx={{ fontSize: '0.75rem' }}
          >
            Clear All
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <DataGridPage<Manufacturer, ManufacturerFilters>
      title="Manufacturer Management"
      addButtonText="Add Manufacturer"
      addButtonPath="/manufacturer-profile"
      hideAddButton={!canManageManufacturers}
      columnDefs={columnDefs}
      response={manufacturersResponse}
      error={error}
      pagination={pagination}
      filters={filters}
      filtersExpanded={filtersExpanded}
      gridContainerRef={gridContainerRef}
      handlePageChange={handlePageChange}
      handlePageSizeChange={handlePageSizeChange}
      handleSortChanged={onSortChanged}
      handleNextPageHover={handleNextPageHover}
      setFiltersExpanded={setFiltersExpanded}
      renderFilterControls={renderFilterControls}
      navigate={navigate}
    />
  );
};

export default ManufacturerManagementPage;
